import React, { useEffect, useState, useCallback } from 'react';
import Axios from 'axios';
import {
  Button,
  Modal,
  Form,
  Input,
  message,
  Typography,
  Popconfirm,
  Spin,
  Upload,
  Divider,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  HolderOutlined,
  UploadOutlined,
  ClockCircleOutlined,
  UnorderedListOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
// react-beautiful-dnd は保守終了 + React 18 StrictMode 非対応のため、後継フォーク @hello-pangea/dnd へ移行（API互換）
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { PageContainer, StatusBadge, EmptyState } from '../../common';
import './ArticleSeriesListPage.css';
// Hero 画像（記事トップ）
import heroImg from '../../../assets/article_top_hero.png';

const { Title, Text } = Typography;

// 編・カテゴリ追加時の仮IDを発行（保存時にサーバー側で正式なedition_code/category_codeを生成）
let _tmpIdCounter = 1;
const tmpId = () => `__tmp_${Date.now()}_${_tmpIdCounter++}`;

// 特徴（features）の入力行は常に3行扱い。既存データを3行へパディングするヘルパー
const padFeatures = (features) => {
  const base = (features || []).map((f) => ({
    feature_title: (f && f.feature_title) || '',
    feature_description: (f && f.feature_description) || '',
  }));
  while (base.length < 3) {
    base.push({ feature_title: '', feature_description: '' });
  }
  return base.slice(0, 3);
};

// 合計時間（分）を「約H時間M分 / 約M分」表記へ整形
const formatMinutes = (totalMinutes) => {
  const m = Number(totalMinutes) || 0;
  if (m <= 0) return '';
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h === 0) return `約${min}分`;
  return `約${h}時間${min}分`;
};

// 入口番号を 2 桁ゼロ埋め（01, 02, …）に整形
const padNumber = (index) => String(index + 1).padStart(2, '0');

// シリーズグループ一覧（記事トップページ / 「シリーズ設定」画面）
// - 閲覧者（role 1,2,3,7）と管理者（role 9）の両方が利用。管理操作は role 9 のみ表示。
// - 階層構造: シリーズグループ（入口）→ シリーズ（2列カード）の 2 段。
// - 管理者: グループの新規作成・編集・ドラッグ並べ替え／グループ内シリーズの新規・編集・ドラッグ並べ替え。
//   シリーズ編集モーダル内で編・カテゴリも管理。
function ArticleSeriesListPage(props) {
  // 新データソース: シリーズグループ（各グループ内に series 配列を持つ）
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  // 管理者判定（role 9 のみ管理操作を表示）
  const role =
    props.user && props.user.userData ? props.user.userData.role : undefined;
  const isAdmin = role === 9;

  // ─────────────────────────────────────
  // グループ 新規作成 / 編集モーダル
  // ─────────────────────────────────────
  const [groupCreateOpen, setGroupCreateOpen] = useState(false);
  const [groupCreateForm] = Form.useForm();
  const [groupEditOpen, setGroupEditOpen] = useState(false);
  const [groupEditForm] = Form.useForm();
  const [editingGroup, setEditingGroup] = useState(null); // { _id, group_code }

  // ─────────────────────────────────────
  // シリーズ 新規作成モーダル
  // ─────────────────────────────────────
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  // 新規作成時の親グループコード（「＋新しいシリーズ」を押したグループのコード）
  const [createGroupCode, setCreateGroupCode] = useState(null);
  // 新規作成時に特徴・ステージング画像を保持
  const [createFeatures, setCreateFeatures] = useState(padFeatures([]));
  const [createImageFile, setCreateImageFile] = useState(null);

  // ─────────────────────────────────────
  // シリーズ編集モーダル（シリーズ＋編＋カテゴリ）
  // ─────────────────────────────────────
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null); // { _id, series_code, series_name, ..., editions, categories, features, thumbnail }
  const [uploadingImage, setUploadingImage] = useState(false);

  // 一覧取得（role をクエリで渡す＝閲覧者は公開シリーズのみ取得）
  const fetchGroups = useCallback(() => {
    setLoading(true);
    const roleQuery = role !== undefined ? `?role=${role}` : '';
    Axios.get(`/api/articleSeriesGroup${roleQuery}`)
      .then((res) => {
        if (res.data && res.data.success) {
          setGroups(res.data.groups || []);
        } else {
          message.error('シリーズグループ一覧の取得に失敗しました');
        }
      })
      .catch((err) => {
        console.log(err);
        message.error('シリーズグループ一覧の取得に失敗しました');
      })
      .finally(() => setLoading(false));
  }, [role]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  // ─────────────────────────────────────
  // グループ 新規作成
  // ─────────────────────────────────────
  const onGroupCreate = (values) => {
    Axios.post('/api/articleSeriesGroup', {
      group_code: values.group_code,
      theme: values.theme || '',
      target: values.target || '',
      field: values.field || '',
      card_description: values.card_description || '',
    })
      .then((res) => {
        if (res.data && res.data.success) {
          message.success('シリーズグループを作成しました');
          setGroupCreateOpen(false);
          groupCreateForm.resetFields();
          fetchGroups();
        } else {
          message.error(res.data.error || 'シリーズグループの作成に失敗しました');
        }
      })
      .catch((err) => {
        const data = err.response && err.response.data;
        // group_code 重複は該当フィールドにエラー表示
        if (data && data.field === 'group_code') {
          groupCreateForm.setFields([
            {
              name: 'group_code',
              errors: [data.error || 'このグループコードは既に使われています'],
            },
          ]);
          return;
        }
        message.error(
          (data && data.error) || 'シリーズグループの作成に失敗しました'
        );
      });
  };

  // ─────────────────────────────────────
  // グループ 編集モーダル開く
  // ─────────────────────────────────────
  const openGroupEdit = (group) => {
    setEditingGroup({ _id: group._id, group_code: group.group_code });
    groupEditForm.setFieldsValue({
      theme: group.theme || '',
      target: group.target || '',
      field: group.field || '',
      card_description: group.card_description || '',
    });
    setGroupEditOpen(true);
  };

  // ─────────────────────────────────────
  // グループ 編集保存
  // ─────────────────────────────────────
  const onGroupEdit = (values) => {
    if (!editingGroup) return;
    Axios.patch(`/api/articleSeriesGroup/${editingGroup._id}`, {
      theme: values.theme || '',
      target: values.target || '',
      field: values.field || '',
      card_description: values.card_description || '',
    })
      .then((res) => {
        if (res.data && res.data.success) {
          message.success('シリーズグループを保存しました');
          setGroupEditOpen(false);
          setEditingGroup(null);
          fetchGroups();
        } else {
          message.error(res.data.error || 'シリーズグループの保存に失敗しました');
        }
      })
      .catch((err) => {
        const data = err.response && err.response.data;
        message.error(
          (data && data.error) || 'シリーズグループの保存に失敗しました'
        );
      });
  };

  // ─────────────────────────────────────
  // グループ並べ替え（ドラッグ・管理者のみ）
  // ─────────────────────────────────────
  const onGroupDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const newGroups = Array.from(groups);
    const [moved] = newGroups.splice(result.source.index, 1);
    newGroups.splice(result.destination.index, 0, moved);

    // 楽観的更新
    setGroups(newGroups);

    // サーバーへ一括再採番リクエスト
    const orderedIds = newGroups.map((g) => g._id);
    Axios.patch('/api/articleSeriesGroup/reorder', { orderedIds })
      .then((res) => {
        if (!(res.data && res.data.success)) {
          message.error('グループ順序の保存に失敗しました');
          fetchGroups();
        }
      })
      .catch((err) => {
        console.log(err);
        message.error('グループ順序の保存に失敗しました');
        fetchGroups();
      });
  };

  // ─────────────────────────────────────
  // シリーズ並べ替え（同一グループ内・ドラッグ・管理者のみ）
  // ─────────────────────────────────────
  // 各グループの Droppable は droppableId = 'series_{group_code}' でスコープ。
  // @hello-pangea/dnd がグループ間移動を報告しても無視（同一 Droppable 内のみ処理）。
  const onSeriesDragEnd = (group) => (result) => {
    if (!result.destination) return;
    // グループ間移動は許可しない（別 Droppable へのドロップは無視）
    if (result.destination.droppableId !== result.source.droppableId) return;
    if (result.destination.index === result.source.index) return;

    const currentSeries = Array.from(group.series || []);
    const [moved] = currentSeries.splice(result.source.index, 1);
    currentSeries.splice(result.destination.index, 0, moved);

    // 楽観的更新（該当グループの series のみ差し替え）
    const newGroups = groups.map((g) =>
      g._id === group._id ? { ...g, series: currentSeries } : g
    );
    setGroups(newGroups);

    // サーバーへ当該グループの series の新順序を一括再採番リクエスト
    const orderedIds = currentSeries.map((s) => s._id);
    Axios.patch('/api/articleSeries/reorder', { orderedIds })
      .then((res) => {
        if (!(res.data && res.data.success)) {
          message.error('シリーズ順序の保存に失敗しました');
          fetchGroups();
        }
      })
      .catch((err) => {
        console.log(err);
        message.error('シリーズ順序の保存に失敗しました');
        fetchGroups();
      });
  };

  // ─────────────────────────────────────
  // シリーズ新規作成（親グループのコードをプリセット）
  // ─────────────────────────────────────
  const openCreate = (group) => {
    setCreateGroupCode(group.group_code);
    setCreateOpen(true);
  };

  const onCreate = (values) => {
    // 特徴の空行を除去（順序維持・先頭タイトル必須は下のバリデーションで担保）
    const featuresForSave = createFeatures
      .filter(
        (f) =>
          (f.feature_title && f.feature_title.trim()) ||
          (f.feature_description && f.feature_description.trim())
      )
      .map((f) => ({
        feature_title: (f.feature_title || '').trim(),
        feature_description: (f.feature_description || '').trim(),
      }));

    // 本シリーズの特徴は最低1つ（先頭のタイトル）必須
    if (featuresForSave.length === 0 || !featuresForSave[0].feature_title) {
      message.error('本シリーズの特徴を1つ以上入力してください');
      return;
    }

    // 親グループのコードを group_code として送信（入口の主題等の4項目はグループ側が持つため送らない）
    const payload = {
      ...values,
      group_code: createGroupCode,
      features: featuresForSave,
    };

    Axios.post('/api/articleSeries', payload)
      .then((res) => {
        if (res.data && res.data.success) {
          // 新規作成時にステージングした画像があれば、作成後に続けてアップロード
          const newId =
            res.data.series && res.data.series._id
              ? res.data.series._id
              : null;
          if (createImageFile && newId) {
            const fd = new FormData();
            fd.append('image', createImageFile);
            Axios.post(`/api/articleSeries/${newId}/image`, fd)
              .then(() => {
                message.success('シリーズを作成しました（メイン画像も設定）');
              })
              .catch(() => {
                message.warning(
                  'シリーズは作成しましたが、メイン画像の設定に失敗しました。編集画面から再設定してください'
                );
              })
              .finally(() => {
                closeCreate();
                fetchGroups();
              });
            return;
          }
          message.success('シリーズを作成しました');
          closeCreate();
          fetchGroups();
        } else {
          message.error(res.data.error || 'シリーズの作成に失敗しました');
        }
      })
      .catch((err) => {
        const msg =
          (err.response && err.response.data && err.response.data.error) ||
          'シリーズの作成に失敗しました';
        message.error(msg);
      });
  };

  const closeCreate = () => {
    setCreateOpen(false);
    setCreateGroupCode(null);
    createForm.resetFields();
    setCreateFeatures(padFeatures([]));
    setCreateImageFile(null);
  };

  // ─────────────────────────────────────
  // シリーズ編集モーダル開く
  // ─────────────────────────────────────
  const openEdit = (series) => {
    // 編集中の状態を別オブジェクトとして複製（参照分離）
    setEditing({
      _id: series._id,
      series_code: series.series_code,
      series_name: series.series_name,
      series_description: series.series_description || '',
      total_episodes_planned: series.total_episodes_planned || undefined,
      // 閲覧可能ロール（Ph.5 で正式開放: role 1, 2, 3, 7, 9）。
      // 実際の閲覧制御は記事単位の is_public（サーバー側 role フィルタ）が担う
      visibility_role: series.visibility_role || [1, 2, 3, 7, 9],
      active: series.active !== false,
      // 本シリーズの特徴（3行扱い）
      features: padFeatures(series.features),
      // メイン画像（サムネール）
      thumbnail: series.thumbnail || '',
      editions: (series.editions || []).map((e) => ({
        edition_code: e.edition_code,
        edition_name: e.edition_name,
        edition_subtitle: e.edition_subtitle || '',
        edition_description: e.edition_description || '',
      })),
      categories: (series.categories || []).map((c) => ({
        category_code: c.category_code,
        category_name: c.category_name,
        category_subtitle: c.category_subtitle || '',
        edition_code: c.edition_code,
      })),
    });
    setEditOpen(true);
  };

  // 編集中のフィールド更新ヘルパー
  const updateEditing = (patch) => {
    setEditing((prev) => ({ ...prev, ...patch }));
  };

  // 特徴の行更新（新規・編集の両モーダル共通）
  const updateFeatureField = (list, setList, index, field, value) => {
    const next = list.map((f, i) =>
      i === index ? { ...f, [field]: value } : f
    );
    setList(next);
  };

  // ─────────────────────────────────────
  // メイン画像アップロード（編集モーダル・既存シリーズ）
  // ─────────────────────────────────────
  const onUploadImage = (file) => {
    if (!editing || !editing._id) return false;
    if (file.size > 10 * 1024 * 1024) {
      message.error('画像サイズは10MB以下にしてください');
      return false;
    }
    const fd = new FormData();
    fd.append('image', file);
    setUploadingImage(true);
    Axios.post(`/api/articleSeries/${editing._id}/image`, fd)
      .then((res) => {
        if (res.data && res.data.success) {
          message.success('メイン画像を設定しました');
          updateEditing({ thumbnail: res.data.thumbnail });
        } else {
          message.error(
            (res.data && res.data.error) || 'メイン画像の設定に失敗しました'
          );
        }
      })
      .catch((err) => {
        const msg =
          (err.response && err.response.data && err.response.data.error) ||
          'メイン画像の設定に失敗しました';
        message.error(msg);
      })
      .finally(() => setUploadingImage(false));
    return false; // 自動アップロードを抑止（手動で axios 送信）
  };

  // ─────────────────────────────────────
  // 編の操作
  // ─────────────────────────────────────
  const addEdition = () => {
    const newEdition = {
      edition_code: tmpId(), // 仮ID（保存時にサーバー側で 'ed_xxxx' を採番）
      edition_name: '',
      edition_subtitle: '',
      edition_description: '',
    };
    updateEditing({ editions: [...editing.editions, newEdition] });
  };

  const removeEdition = (index) => {
    const target = editing.editions[index];
    // この編に属するカテゴリがあるか確認
    const hasCategories = editing.categories.some(
      (c) => c.edition_code === target.edition_code
    );
    if (hasCategories) {
      message.warning(
        'この編に属するカテゴリがあるため削除できません。先にカテゴリを別の編に移すか削除してください'
      );
      return;
    }
    const newEditions = editing.editions.filter((_, i) => i !== index);
    updateEditing({ editions: newEditions });
  };

  const updateEditionField = (index, field, value) => {
    const newEditions = editing.editions.map((e, i) =>
      i === index ? { ...e, [field]: value } : e
    );
    updateEditing({ editions: newEditions });
  };

  const onEditionDragEnd = (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;
    const newEditions = Array.from(editing.editions);
    const [moved] = newEditions.splice(result.source.index, 1);
    newEditions.splice(result.destination.index, 0, moved);
    updateEditing({ editions: newEditions });
  };

  // ─────────────────────────────────────
  // カテゴリの操作
  // ─────────────────────────────────────
  const addCategory = (edition_code) => {
    const newCategory = {
      category_code: tmpId(),
      category_name: '',
      category_subtitle: '',
      edition_code,
    };
    updateEditing({ categories: [...editing.categories, newCategory] });
  };

  const removeCategory = (idx) => {
    const newCategories = editing.categories.filter((_, i) => i !== idx);
    updateEditing({ categories: newCategories });
  };

  const updateCategoryField = (idx, field, value) => {
    const newCategories = editing.categories.map((c, i) =>
      i === idx ? { ...c, [field]: value } : c
    );
    updateEditing({ categories: newCategories });
  };

  // 同じ編内でのカテゴリ並べ替え
  const onCategoryDragEnd = (edition_code) => (result) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    // editing.categories の中で この edition_code のものだけ並べ替えて全体に反映
    const inEdition = editing.categories.filter(
      (c) => c.edition_code === edition_code
    );
    const others = editing.categories.filter(
      (c) => c.edition_code !== edition_code
    );

    const reordered = Array.from(inEdition);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);

    // 全体のカテゴリ配列は「他編のカテゴリ + 並び替え後のこの編カテゴリ」
    // ただし元のグルーピング順序を維持するため、最初に別編のカテゴリを残し、その後に並び替え後を続ける
    // ※ 表示は edition_code でフィルタするため、保存上の順序は同じ edition_code 内での相対順序が重要
    updateEditing({ categories: [...others, ...reordered] });
  };

  // ─────────────────────────────────────
  // 編集内容を保存
  // ─────────────────────────────────────
  const onSaveEdit = () => {
    if (!editing) return;

    // バリデーション
    if (!editing.series_name || !editing.series_name.trim()) {
      message.error('シリーズ名は必須です');
      return;
    }
    // 編名の必須＋重複チェック
    const editionNames = editing.editions.map((e) => e.edition_name.trim());
    if (editionNames.some((n) => !n)) {
      message.error('編名が空欄の項目があります');
      return;
    }
    if (new Set(editionNames).size !== editionNames.length) {
      message.error('編名が重複しています');
      return;
    }
    // カテゴリ名の必須＋重複チェック
    const categoryNames = editing.categories.map((c) =>
      c.category_name.trim()
    );
    if (categoryNames.some((n) => !n)) {
      message.error('カテゴリ名が空欄の項目があります');
      return;
    }
    if (new Set(categoryNames).size !== categoryNames.length) {
      message.error('カテゴリ名が重複しています');
      return;
    }

    // 本シリーズの特徴: 空行を除去（順序維持）。最低1つ（先頭タイトル）必須
    const featuresForSave = editing.features
      .filter(
        (f) =>
          (f.feature_title && f.feature_title.trim()) ||
          (f.feature_description && f.feature_description.trim())
      )
      .map((f) => ({
        feature_title: (f.feature_title || '').trim(),
        feature_description: (f.feature_description || '').trim(),
      }));
    if (featuresForSave.length === 0 || !featuresForSave[0].feature_title) {
      message.error('本シリーズの特徴を1つ以上入力してください');
      return;
    }

    // 仮IDの編・カテゴリは送信時に未定義にしてサーバー側で採番させる
    const editionsForSave = editing.editions.map((e) => {
      const code = e.edition_code.startsWith('__tmp_')
        ? undefined
        : e.edition_code;
      return {
        ...(code !== undefined ? { edition_code: code } : {}),
        edition_name: e.edition_name.trim(),
        edition_subtitle: e.edition_subtitle || '',
        edition_description: e.edition_description || '',
      };
    });

    // カテゴリは編の仮IDを実IDに置換できないので、編が新規(__tmp_)の場合は
    // サーバー側でedition_codeを自動採番した後、カテゴリの edition_code もマッピングし直す必要がある。
    // 本実装では「編の追加」と「カテゴリの追加」を同時に行う場合、
    // 一度編のみ保存→再取得→改めてカテゴリを追加する2段階運用を想定。
    // → クライアント側で「仮IDの編にカテゴリを追加しようとしたら警告」する。
    const tmpEditionCodes = new Set(
      editing.editions
        .filter((e) => e.edition_code.startsWith('__tmp_'))
        .map((e) => e.edition_code)
    );
    const categoriesWithTmpEdition = editing.categories.filter((c) =>
      tmpEditionCodes.has(c.edition_code)
    );
    if (categoriesWithTmpEdition.length > 0) {
      message.warning(
        '新規追加した編にカテゴリを紐付けることはできません。先に「保存」ボタンで編を保存してから、改めてカテゴリを追加してください'
      );
      return;
    }

    const categoriesForSave = editing.categories.map((c) => {
      const code = c.category_code.startsWith('__tmp_')
        ? undefined
        : c.category_code;
      return {
        ...(code !== undefined ? { category_code: code } : {}),
        category_name: c.category_name.trim(),
        category_subtitle: c.category_subtitle || '',
        edition_code: c.edition_code,
      };
    });

    // group_code は送信しない（サーバー側で無視される＝グループ移動はこの画面では行わない）
    const payload = {
      series_name: editing.series_name.trim(),
      series_description: editing.series_description || '',
      total_episodes_planned: editing.total_episodes_planned || undefined,
      visibility_role: editing.visibility_role,
      active: editing.active,
      features: featuresForSave,
      editions: editionsForSave,
      categories: categoriesForSave,
    };

    Axios.patch(`/api/articleSeries/${editing._id}`, payload)
      .then((res) => {
        if (res.data && res.data.success) {
          // 名前変更によって Article の非正規化フィールドが更新された場合は通知
          const updated = res.data.updated_articles_count || 0;
          if (updated > 0) {
            message.success(
              `シリーズを保存しました（記事の表示名 ${updated} 件を一緒に更新）`
            );
          } else {
            message.success('シリーズを保存しました');
          }
          setEditOpen(false);
          setEditing(null);
          fetchGroups();
        } else {
          message.error(res.data.error || '保存に失敗しました');
        }
      })
      .catch((err) => {
        const data = err.response && err.response.data;
        // 削除しようとした 編 / カテゴリに記事が残っているケースは Modal で詳細表示
        if (
          data &&
          (Array.isArray(data.blocking_editions) ||
            Array.isArray(data.blocking_categories))
        ) {
          const lines = [];
          if (Array.isArray(data.blocking_editions)) {
            data.blocking_editions.forEach((b) => {
              lines.push(
                `編「${b.edition_name}」に ${b.count} 件の記事が残っています`
              );
            });
          }
          if (Array.isArray(data.blocking_categories)) {
            data.blocking_categories.forEach((b) => {
              lines.push(
                `カテゴリ「${b.category_name}」に ${b.count} 件の記事が残っています`
              );
            });
          }
          Modal.error({
            title: '削除できません',
            content: (
              <div>
                <p style={{ marginBottom: 'var(--space-2)' }}>{data.error}</p>
                <ul style={{ paddingLeft: 18 }}>
                  {lines.map((l, i) => (
                    <li key={i}>{l}</li>
                  ))}
                </ul>
                <p
                  style={{
                    marginTop: 'var(--space-3)',
                    color: 'var(--grey-65)',
                    fontSize: 'var(--text-xs)',
                  }}
                >
                  シリーズホームの「編集モード」で該当記事を別の 編
                  ／カテゴリへ移動するか、記事自体を削除してから再度お試しください。
                </p>
              </div>
            ),
          });
          return;
        }
        const msg = (data && data.error) || '保存に失敗しました';
        message.error(msg);
      });
  };

  // ─────────────────────────────────────
  // レンダリング: シリーズカード（グループ内の 2 列グリッド要素）
  // ─────────────────────────────────────
  const renderSeriesCardInner = (series) => {
    const minutesText = formatMinutes(series.total_minutes);
    const articleCount = series.article_count || 0;
    return (
      <>
        {/* サムネイル（未設定時は淡いプレースホルダ） */}
        <div className="asl-series-tile-thumb">
          {series.thumbnail ? (
            <img src={series.thumbnail} alt={series.series_name} loading="lazy" />
          ) : null}
        </div>
        <div className="asl-series-tile-body">
          <div className="asl-series-card-name">{series.series_name}</div>
          <div className="asl-series-tile-meta">
            <span className="asl-meta-chip">
              <UnorderedListOutlined />全{articleCount}回
            </span>
            {minutesText && (
              <span className="asl-meta-chip">
                <ClockCircleOutlined />
                {minutesText}
              </span>
            )}
          </div>
        </div>
      </>
    );
  };

  // 空のシリーズグループ（サンプルの空タイル: アイコン + 案内文・横幅いっぱい）
  const renderEmptyTile = () => (
    <div className="asl-series-tile-empty">
      <FileTextOutlined />
      <span>このシリーズグループの読み物、いまつくっています</span>
    </div>
  );

  // 閲覧者モード: シリーズカード（リンクのみ・ドラッグなし）
  const renderViewerSeriesCard = (series) => (
    <a
      key={series._id}
      className="asl-series-card"
      href={`/article/${series.series_code}`}
    >
      {renderSeriesCardInner(series)}
    </a>
  );

  // 管理者モード: ドラッグ可能 + 編集ボタン付きシリーズカード
  const renderAdminSeriesCard = (series, index) => (
    <Draggable draggableId={series._id} index={index} key={series._id}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          className={
            'asl-series-card asl-series-card--admin' +
            (snapshot.isDragging ? ' asl-series-card-dragging' : '')
          }
          style={{ ...provided.draggableProps.style }}
          onClick={() => props.history.push(`/article/${series.series_code}`)}
        >
          <div className="asl-series-card-admin-bar">
            <span
              {...provided.dragHandleProps}
              className="asl-series-card-handle"
              title="ドラッグして並べ替え"
              onClick={(e) => e.stopPropagation()}
            >
              <HolderOutlined />
            </span>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openEdit(series);
              }}
            >
              編集
            </Button>
            {series.active === false && (
              <StatusBadge tone="error">非公開</StatusBadge>
            )}
          </div>
          {renderSeriesCardInner(series)}
        </div>
      )}
    </Draggable>
  );

  // ─────────────────────────────────────
  // レンダリング: グループブロック（pillar block）の中身（左情報 + 右シリーズグリッド）
  // ─────────────────────────────────────
  // 管理者の操作バー（ドラッグハンドル・編集ボタン）は呼び出し側で前置する。
  const renderGroupInner = (group, index) => {
    const title = group.theme || group.group_code;
    const target = (group.target || '').trim();
    const fieldText = (group.field || '').trim();
    const cardDesc = group.card_description || '';
    const series = group.series || [];

    return (
      <>
        {/* 左カラム: グループ（入口）情報 */}
        <div className="asl-pillar-block-info">
          <div className="asl-pillar-block-head">
            <span className="asl-pillar-block-number">{padNumber(index)}</span>
            <h2 className="asl-pillar-block-title">{title}</h2>
          </div>
          {(target || fieldText) && (
            <p className="asl-pillar-block-meta">
              {target && <strong>{target}</strong>}
              {target && fieldText ? ' ／ ' : ''}
              {fieldText}
            </p>
          )}
          {cardDesc && <p className="asl-pillar-block-role">{cardDesc}</p>}
        </div>

        {/* 右カラム: シリーズカードの 2 列グリッド */}
        <div className="asl-pillar-block-content">
          {isAdmin ? (
            // 管理者: グループ内シリーズのドラッグ並べ替え（Droppable はグループ単位にスコープ）
            <DragDropContext onDragEnd={onSeriesDragEnd(group)}>
              <Droppable
                droppableId={`series_${group.group_code}`}
                direction="vertical"
              >
                {(provided) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className="asl-series-grid"
                  >
                    {series.map((s, idx) => renderAdminSeriesCard(s, idx))}
                    {provided.placeholder}
                    {series.length === 0 && renderEmptyTile()}
                    {/* ＋新しいシリーズ（このグループのコードをプリセット） */}
                    <button
                      type="button"
                      className="asl-series-add-card"
                      onClick={() => openCreate(group)}
                    >
                      <PlusOutlined /> 新しいシリーズ
                    </button>
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          ) : (
            // 閲覧者: シリーズカードのグリッド（ドラッグなし）
            <div className="asl-series-grid">
              {series.length === 0
                ? renderEmptyTile()
                : series.map((s) => renderViewerSeriesCard(s))}
            </div>
          )}
        </div>
      </>
    );
  };

  // 閲覧者モード: グループブロック（ドラッグなし）
  const renderViewerGroup = (group, index) => (
    <section
      key={group._id}
      id={`asl-pillar-${group.group_code}`}
      className="asl-pillar-block"
    >
      {renderGroupInner(group, index)}
    </section>
  );

  // 管理者モード: ドラッグ可能 + 操作バー（ハンドル・編集・非公開バッジ）付きグループブロック
  const renderAdminGroup = (group, index) => (
    <Draggable draggableId={group._id} index={index} key={group._id}>
      {(provided, snapshot) => (
        <section
          ref={provided.innerRef}
          {...provided.draggableProps}
          id={`asl-pillar-${group.group_code}`}
          className={
            'asl-pillar-block' +
            (snapshot.isDragging ? ' asl-pillar-dragging' : '')
          }
          style={{ ...provided.draggableProps.style }}
        >
          {/* 管理者操作バー（グループ全体に対して。先頭に全幅で配置） */}
          <div
            className="asl-pillar-admin-bar"
            style={{ gridColumn: '1 / -1' }}
          >
            <span
              {...provided.dragHandleProps}
              className="asl-pillar-handle"
              title="ドラッグして並べ替え"
            >
              <HolderOutlined style={{ fontSize: 'var(--icon-md)' }} />
            </span>
            <Button
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openGroupEdit(group);
              }}
            >
              グループ編集
            </Button>
            {group.active === false && (
              <span className="asl-pillar-badges">
                <StatusBadge tone="error">非公開</StatusBadge>
              </span>
            )}
          </div>
          {renderGroupInner(group, index)}
        </section>
      )}
    </Draggable>
  );

  // 編集モーダル: 編・カテゴリ管理セクション
  const renderEditionsSection = () => {
    if (!editing) return null;
    return (
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--space-2)',
          }}
        >
          <Title level={5} style={{ margin: 0 }}>
            編（編）
          </Title>
          <Button
            size="small"
            icon={<PlusOutlined />}
            onClick={addEdition}
          >
            編を追加
          </Button>
        </div>

        <DragDropContext onDragEnd={onEditionDragEnd}>
          <Droppable droppableId="editions-list">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {editing.editions.length === 0 && (
                  <EmptyState message="編が登録されていません" />
                )}
                {editing.editions.map((ed, idx) => (
                  <Draggable
                    draggableId={'edition_' + ed.edition_code}
                    index={idx}
                    key={ed.edition_code}
                  >
                    {(prov, snap) => (
                      <div
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        style={{
                          marginBottom: 'var(--space-4)',
                          padding: 'var(--space-3)',
                          background: 'var(--grey-10)',
                          border: '1px solid var(--grey-20)',
                          borderRadius: 'var(--radius-sm)',
                          ...prov.draggableProps.style,
                          opacity: snap.isDragging ? 0.8 : 1,
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            marginBottom: 'var(--space-2)',
                          }}
                        >
                          <div
                            {...prov.dragHandleProps}
                            style={{ cursor: 'grab', color: 'var(--grey-45)' }}
                            title="ドラッグして並べ替え"
                          >
                            <HolderOutlined />
                          </div>
                          <Input
                            placeholder="編名（例: 概論編）"
                            value={ed.edition_name}
                            onChange={(e) =>
                              updateEditionField(
                                idx,
                                'edition_name',
                                e.target.value
                              )
                            }
                            style={{ flex: 1 }}
                          />
                          <Input
                            placeholder="サブタイトル（任意）"
                            value={ed.edition_subtitle}
                            onChange={(e) =>
                              updateEditionField(
                                idx,
                                'edition_subtitle',
                                e.target.value
                              )
                            }
                            style={{ flex: 1 }}
                          />
                          <Popconfirm
                            title="この編を削除しますか？"
                            onConfirm={() => removeEdition(idx)}
                            okText="削除"
                            cancelText="キャンセル"
                          >
                            <Button
                              danger
                              size="small"
                              icon={<DeleteOutlined />}
                            />
                          </Popconfirm>
                        </div>
                        {/* 編の説明文 */}
                        <Input.TextArea
                          placeholder="編の説明（任意）"
                          value={ed.edition_description}
                          onChange={(e) =>
                            updateEditionField(
                              idx,
                              'edition_description',
                              e.target.value
                            )
                          }
                          rows={2}
                          style={{ marginBottom: 'var(--space-2)' }}
                        />
                        {!ed.edition_code.startsWith('__tmp_') && (
                          <div style={{ paddingLeft: 'var(--space-6)' }}>
                            {renderCategoriesOfEdition(ed.edition_code)}
                          </div>
                        )}
                        {ed.edition_code.startsWith('__tmp_') && (
                          <div
                            style={{
                              paddingLeft: 'var(--space-6)',
                              color: 'var(--grey-65)',
                              fontSize: 'var(--text-xs)',
                            }}
                          >
                            ※ 新しい編にカテゴリを紐付けるには、まず一度「保存」してください
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    );
  };

  // 指定の編に属するカテゴリ一覧（ドラッグ並べ替え対応）
  const renderCategoriesOfEdition = (edition_code) => {
    const inEdition = editing.categories
      .map((c, originalIdx) => ({ ...c, originalIdx }))
      .filter((c) => c.edition_code === edition_code);

    return (
      <div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--space-2)',
          }}
        >
          <Text type="secondary" style={{ fontSize: 'var(--text-xs)' }}>
            カテゴリ（領域）
          </Text>
          <Button
            size="small"
            type="link"
            icon={<PlusOutlined />}
            onClick={() => addCategory(edition_code)}
          >
            カテゴリを追加
          </Button>
        </div>

        <DragDropContext onDragEnd={onCategoryDragEnd(edition_code)}>
          <Droppable droppableId={'cats_' + edition_code}>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {inEdition.length === 0 && (
                  <Text
                    type="secondary"
                    style={{ fontSize: 'var(--text-xs)', fontStyle: 'italic' }}
                  >
                    （カテゴリ未登録）
                  </Text>
                )}
                {inEdition.map((c, idx) => (
                  <Draggable
                    draggableId={'cat_' + c.category_code}
                    index={idx}
                    key={c.category_code}
                  >
                    {(prov, snap) => (
                      <div
                        ref={prov.innerRef}
                        {...prov.draggableProps}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--space-2)',
                          padding: 'var(--space-2)',
                          background: 'var(--white)',
                          border: '1px solid var(--grey-20)',
                          borderRadius: 'var(--radius-sm)',
                          marginBottom: 'var(--space-1)',
                          ...prov.draggableProps.style,
                          opacity: snap.isDragging ? 0.8 : 1,
                        }}
                      >
                        <div
                          {...prov.dragHandleProps}
                          style={{ cursor: 'grab', color: 'var(--grey-45)' }}
                        >
                          <HolderOutlined />
                        </div>
                        <Input
                          size="small"
                          placeholder="カテゴリ名（例: 戦略領域）"
                          value={c.category_name}
                          onChange={(e) =>
                            updateCategoryField(
                              c.originalIdx,
                              'category_name',
                              e.target.value
                            )
                          }
                          style={{ flex: 1 }}
                        />
                        <Input
                          size="small"
                          placeholder="サブタイトル（任意）"
                          value={c.category_subtitle}
                          onChange={(e) =>
                            updateCategoryField(
                              c.originalIdx,
                              'category_subtitle',
                              e.target.value
                            )
                          }
                          style={{ flex: 1 }}
                        />
                        <Popconfirm
                          title="このカテゴリを削除しますか？"
                          onConfirm={() => removeCategory(c.originalIdx)}
                          okText="削除"
                          cancelText="キャンセル"
                        >
                          <Button
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                          />
                        </Popconfirm>
                      </div>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      </div>
    );
  };

  // ─────────────────────────────────────
  // 特徴セクション（新規・編集 両モーダル共通）
  // ─────────────────────────────────────
  const renderFeaturesSection = (list, setList) => (
    <div>
      <Text strong style={{ display: 'block', marginBottom: 'var(--space-2)' }}>
        本シリーズの特徴（必須・1つ以上）
      </Text>
      {list.map((f, idx) => (
        <div
          key={idx}
          style={{
            display: 'flex',
            gap: 'var(--space-2)',
            marginBottom: 'var(--space-2)',
          }}
        >
          <Input
            placeholder={`特徴${idx + 1}のタイトル`}
            value={f.feature_title}
            onChange={(e) =>
              updateFeatureField(
                list,
                setList,
                idx,
                'feature_title',
                e.target.value
              )
            }
            style={{ flex: '0 0 38%' }}
          />
          <Input
            placeholder="特徴の説明"
            value={f.feature_description}
            onChange={(e) =>
              updateFeatureField(
                list,
                setList,
                idx,
                'feature_description',
                e.target.value
              )
            }
            style={{ flex: 1 }}
          />
        </div>
      ))}
    </div>
  );

  // ─────────────────────────────────────
  // メインレンダリング
  // ─────────────────────────────────────
  return (
    <PageContainer style={{ paddingTop: 'var(--space-6)' }}>
      <div className="asl-root">
        {/* パンくず（シリーズページと余白・色・名称を統一） */}
        <div className="asl-breadcrumb">
          <a href="/">Home</a>
          <span className="asl-breadcrumb-sep">›</span>
          <span className="asl-breadcrumb-current">記事一覧</span>
        </div>

        {/* Hero ブロック */}
        <div className="asl-hero">
          <div className="asl-hero-image">
            <img src={heroImg} alt="記事トップ" />
          </div>
          <div className="asl-hero-body">
            <h1 className="asl-hero-headline">
              人と組織のことを、もっと深く。学びを行動に変えていく。
            </h1>
            <p className="asl-hero-lead">
              自分自身のこと、まわりの人との関わり、そしてチームや組織のこと。人と組織にまつわるさまざまな学びを、いつでもじっくり読むことができます。マネジメントや組織づくりといった大きなテーマから、日々のコミュニケーションの小さなヒントまで。読んで終わりではなく、明日の行動が少し変わる——そんなきっかけになる記事を目指しています。気になるテーマから、ご自身のペースで読み進めてみてください。
            </p>
          </div>
        </div>

        {/* Hero 下の 2 カラムレイアウト（左サイドバー + グループブロック） */}
        <div className="asl-layout-grid">
          {/* ───── 左サイドバー ───── */}
          <aside className="asl-sidebar">
            {/* テーマ一覧（シリーズグループの目次） */}
            <div className="asl-side-card">
              <div className="asl-side-card-head">テーマ一覧</div>
              <ul className="asl-pillar-toc">
                {groups.map((g, idx) => (
                  <li key={g._id}>
                    <a
                      href={`#asl-pillar-${g.group_code}`}
                      onClick={(e) => {
                        // 固定ナビ配下 / SPA でも確実にスクロールさせるため scrollIntoView を明示。
                        // オフセットは対象側の scroll-margin-top（CSS）で吸収。
                        e.preventDefault();
                        const el = document.getElementById(
                          `asl-pillar-${g.group_code}`
                        );
                        if (el) {
                          el.scrollIntoView({
                            behavior: 'smooth',
                            block: 'start',
                          });
                        }
                      }}
                    >
                      <span className="asl-pillar-toc-num">
                        {padNumber(idx)}
                      </span>
                      <span>{g.theme || g.group_code}</span>
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* ───── メイン領域: グループブロック群 ───── */}
          <main>
            {/* 管理者のみ: 新規シリーズグループ追加ボタン（本文導入テキストは撤去） */}
            {isAdmin && (
              <div
                className="asl-section-head"
                style={{ justifyContent: 'flex-end' }}
              >
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setGroupCreateOpen(true)}
                >
                  新しいシリーズグループ
                </Button>
              </div>
            )}

            {loading ? (
              <div style={{ textAlign: 'center', padding: 'var(--space-10)' }}>
                <Spin />
              </div>
            ) : groups.length === 0 ? (
              <EmptyState
                message={
                  isAdmin
                    ? 'シリーズグループがありません。「新しいシリーズグループ」から作成してください'
                    : 'シリーズグループがありません'
                }
                style={{ marginTop: 'var(--space-10)' }}
              />
            ) : isAdmin ? (
              // 管理者: ドラッグ並べ替え対応のグループブロック群
              <DragDropContext onDragEnd={onGroupDragEnd}>
                <Droppable droppableId="groups-list">
                  {(provided) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                    >
                      {groups.map((g, idx) => renderAdminGroup(g, idx))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            ) : (
              // 閲覧者: グループブロック群（ドラッグなし）
              <div>
                {groups.map((g, idx) => renderViewerGroup(g, idx))}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* ───── グループ 新規作成モーダル ───── */}
      <Modal
        title="新しいシリーズグループ"
        open={groupCreateOpen}
        onCancel={() => {
          setGroupCreateOpen(false);
          groupCreateForm.resetFields();
        }}
        onOk={() => groupCreateForm.submit()}
        okText="作成"
        cancelText="キャンセル"
        destroyOnHidden
      >
        <Form
          form={groupCreateForm}
          layout="vertical"
          onFinish={onGroupCreate}
        >
          <Form.Item
            label="グループコード（slug）"
            name="group_code"
            rules={[
              { required: true, message: 'グループコードは必須です' },
              {
                pattern: /^[a-z0-9-]+$/,
                message: '英小文字・数字・ハイフンのみ使用可能',
              },
            ]}
            extra="例: self-understanding （作成後は変更不可）"
          >
            <Input placeholder="self-understanding" />
          </Form.Item>
          <Form.Item label="入口の主題（カード見出し）" name="theme">
            <Input placeholder="自分と向き合う" />
          </Form.Item>
          <Form.Item label="対象者" name="target">
            <Input placeholder="受診者本人" />
          </Form.Item>
          <Form.Item label="対象分野" name="field">
            <Input placeholder="自己理解・内省支援" />
          </Form.Item>
          <Form.Item
            label="カード説明（一覧カード用の短い紹介）"
            name="card_description"
          >
            <Input.TextArea
              rows={2}
              placeholder="入口カードに表示する短い紹介文…"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* ───── グループ 編集モーダル ───── */}
      <Modal
        title={
          editingGroup
            ? `シリーズグループ編集: ${editingGroup.group_code}`
            : 'シリーズグループ編集'
        }
        open={groupEditOpen}
        onCancel={() => {
          setGroupEditOpen(false);
          setEditingGroup(null);
        }}
        onOk={() => groupEditForm.submit()}
        okText="保存"
        cancelText="キャンセル"
        destroyOnHidden
      >
        <Form form={groupEditForm} layout="vertical" onFinish={onGroupEdit}>
          <Form.Item label="グループコード">
            <Input value={editingGroup ? editingGroup.group_code : ''} disabled />
          </Form.Item>
          <Form.Item label="入口の主題（カード見出し）" name="theme">
            <Input placeholder="自分と向き合う" />
          </Form.Item>
          <Form.Item label="対象者" name="target">
            <Input placeholder="受診者本人" />
          </Form.Item>
          <Form.Item label="対象分野" name="field">
            <Input placeholder="自己理解・内省支援" />
          </Form.Item>
          <Form.Item
            label="カード説明（一覧カード用の短い紹介）"
            name="card_description"
          >
            <Input.TextArea
              rows={2}
              placeholder="入口カードに表示する短い紹介文…"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* ───── シリーズ 新規作成モーダル ───── */}
      <Modal
        title="新しいシリーズ"
        open={createOpen}
        onCancel={closeCreate}
        onOk={() => createForm.submit()}
        okText="作成"
        cancelText="キャンセル"
        destroyOnHidden
      >
        <Form form={createForm} layout="vertical" onFinish={onCreate}>
          <Form.Item
            label="シリーズコード（URL用slug）"
            name="series_code"
            rules={[
              { required: true, message: 'series_code は必須です' },
              {
                pattern: /^[a-z0-9-]+$/,
                message: '英小文字・数字・ハイフンのみ使用可能',
              },
            ]}
            extra="例: hr-basics-2026 （URL に使われるため、変更不可）"
          >
            <Input placeholder="hr-basics-2026" />
          </Form.Item>
          <Form.Item
            label="シリーズ名"
            name="series_name"
            rules={[{ required: true, message: 'シリーズ名は必須です' }]}
          >
            <Input placeholder="人事担当者向け学習シリーズ" />
          </Form.Item>
          <Form.Item label="説明（リード文）" name="series_description">
            <Input.TextArea rows={4} placeholder="シリーズの説明…" />
          </Form.Item>

          {/* 本シリーズの特徴（3行） */}
          {renderFeaturesSection(createFeatures, setCreateFeatures)}

          <Divider />

          {/* メイン画像（新規作成時はステージング → 作成後に自動アップロード） */}
          <Form.Item label="メイン画像（任意）">
            <Upload
              accept="image/*"
              maxCount={1}
              beforeUpload={(file) => {
                if (file.size > 10 * 1024 * 1024) {
                  message.error('画像サイズは10MB以下にしてください');
                  return Upload.LIST_IGNORE;
                }
                setCreateImageFile(file);
                return false; // 自動アップロード抑止（作成後に送信）
              }}
              onRemove={() => setCreateImageFile(null)}
              listType="picture"
            >
              <Button icon={<UploadOutlined />}>画像を選択</Button>
            </Upload>
            <Text type="secondary" style={{ fontSize: 'var(--text-xs)' }}>
              選択した画像は、シリーズ作成後に自動でメイン画像に設定されます
            </Text>
          </Form.Item>
        </Form>
      </Modal>

      {/* ───── シリーズ 編集モーダル ───── */}
      <Modal
        title={editing ? `シリーズ編集: ${editing.series_name}` : 'シリーズ編集'}
        open={editOpen}
        onCancel={() => {
          setEditOpen(false);
          setEditing(null);
        }}
        onOk={onSaveEdit}
        okText="保存"
        cancelText="キャンセル"
        width={900}
        destroyOnHidden
        styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      >
        {editing && (
          <div>
            {/* シリーズメタ情報 */}
            <Title level={5}>基本情報</Title>
            <Form layout="vertical">
              <Form.Item label="シリーズコード">
                <Input value={editing.series_code} disabled />
              </Form.Item>
              <Form.Item label="シリーズ名" required>
                <Input
                  value={editing.series_name}
                  onChange={(e) =>
                    updateEditing({ series_name: e.target.value })
                  }
                />
              </Form.Item>
              <Form.Item label="説明（リード文）">
                <Input.TextArea
                  rows={3}
                  value={editing.series_description}
                  onChange={(e) =>
                    updateEditing({ series_description: e.target.value })
                  }
                />
              </Form.Item>
            </Form>

            {/* 本シリーズの特徴（3行） */}
            {renderFeaturesSection(editing.features, (next) =>
              updateEditing({ features: next })
            )}

            <Divider />

            {/* メイン画像（既存シリーズはドロップ即時アップロード） */}
            <Text
              strong
              style={{ display: 'block', marginBottom: 'var(--space-2)' }}
            >
              メイン画像（任意）
            </Text>
            {editing.thumbnail && (
              <div style={{ marginBottom: 'var(--space-2)' }}>
                <img
                  src={editing.thumbnail}
                  alt="メイン画像プレビュー"
                  style={{
                    maxWidth: 240,
                    maxHeight: 140,
                    objectFit: 'cover',
                    borderRadius: 'var(--radius-md)',
                    border: '1px solid var(--grey-30)',
                  }}
                />
              </div>
            )}
            <Upload
              accept="image/*"
              showUploadList={false}
              beforeUpload={onUploadImage}
            >
              <Button icon={<UploadOutlined />} loading={uploadingImage}>
                {editing.thumbnail ? '画像を変更' : '画像を設定'}
              </Button>
            </Upload>
            <Text
              type="secondary"
              style={{
                display: 'block',
                marginTop: 'var(--space-1)',
                fontSize: 'var(--text-xs)',
              }}
            >
              画像形式・10MB以下。選択すると即時にアップロードされます。
            </Text>

            <Divider />

            {/* 編＋カテゴリ管理 */}
            {renderEditionsSection()}
          </div>
        )}
      </Modal>
    </PageContainer>
  );
}

export default ArticleSeriesListPage;
